import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Search, Edit, Trash2, TrendingUp, Download, Upload } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TAG_TYPES = [
  { value: 'product', label: 'Product' },
  { value: 'service', label: 'Service' },
  { value: 'rental', label: 'Rental' },
  { value: 'classified', label: 'Classified' },
  { value: 'subscription', label: 'Subscription' },
  { value: 'volunteering', label: 'Volunteering' },
  { value: 'event', label: 'Event' },
  { value: 'customer', label: 'Customer' },
  { value: 'order', label: 'Order' },
  { value: 'blog_post', label: 'Blog Post' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'draft_order', label: 'Draft Order' }
];

const TAG_COLORS = [
  { value: '#3B82F6', label: 'Blue' },
  { value: '#10B981', label: 'Green' },
  { value: '#F59E0B', label: 'Amber' },
  { value: '#EF4444', label: 'Red' },
  { value: '#8B5CF6', label: 'Purple' },
  { value: '#EC4899', label: 'Pink' },
  { value: '#6366F1', label: 'Indigo' },
  { value: '#14B8A6', label: 'Teal' },
  { value: '#F97316', label: 'Orange' },
  { value: '#64748B', label: 'Slate' }
];

const Tags = () => {
  const [tags, setTags] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [currentTag, setCurrentTag] = useState(null);
  const [formData, setFormData] = useState({ name: '', tag_type: 'product', color: null });
  const [importCsvContent, setImportCsvContent] = useState('');

  useEffect(() => {
    fetchTags();
    fetchStats();
  }, [selectedType]);

  const fetchTags = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedType && selectedType !== 'all') {
        params.tag_type = selectedType;
      }
      const response = await axios.get(`${API_URL}/api/tags/`, { params });
      setTags(response.data);
    } catch (error) {
      console.error('Error fetching tags:', error);
      toast.error('Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tags/stats/summary`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleCreateTag = async () => {
    try {
      await axios.post(`${API_URL}/api/tags/`, formData);
      toast.success('Tag created successfully');
      setIsCreateModalOpen(false);
      setFormData({ name: '', tag_type: 'product' });
      fetchTags();
      fetchStats();
    } catch (error) {
      console.error('Error creating tag:', error);
      toast.error(error.response?.data?.detail || 'Failed to create tag');
    }
  };

  const handleUpdateTag = async () => {
    try {
      await axios.put(`${API_URL}/api/tags/${currentTag.id}`, { name: formData.name });
      toast.success('Tag updated successfully');
      setIsEditModalOpen(false);
      setCurrentTag(null);
      setFormData({ name: '', tag_type: 'product' });
      fetchTags();
    } catch (error) {
      console.error('Error updating tag:', error);
      toast.error(error.response?.data?.detail || 'Failed to update tag');
    }
  };

  const handleDeleteTag = async (tag) => {
    if (!window.confirm(`Are you sure you want to delete the tag "${tag.display_name}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/tags/${tag.id}`);
      toast.success('Tag deleted successfully');
      fetchTags();
      fetchStats();
    } catch (error) {
      console.error('Error deleting tag:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete tag');
    }
  };

  const openEditModal = (tag) => {
    setCurrentTag(tag);
    setFormData({ name: tag.display_name, tag_type: tag.tag_type, color: tag.color });
    setIsEditModalOpen(true);
  };

  const handleExport = async () => {
    try {
      const params = selectedType !== 'all' ? `?tag_type=${selectedType}` : '';
      const response = await axios.get(`${API_URL}/api/tags/export/csv${params}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tags_${selectedType}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Tags exported successfully');
    } catch (error) {
      console.error('Error exporting tags:', error);
      toast.error('Failed to export tags');
    }
  };

  const handleImport = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/tags/import/csv`, 
        importCsvContent,
        { headers: { 'Content-Type': 'text/plain' } }
      );
      
      toast.success(`Import complete: ${response.data.imported} imported, ${response.data.skipped} skipped`);
      setIsImportModalOpen(false);
      setImportCsvContent('');
      fetchTags();
      fetchStats();
    } catch (error) {
      console.error('Error importing tags:', error);
      toast.error(error.response?.data?.detail || 'Failed to import tags');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setImportCsvContent(event.target.result);
      };
      reader.readAsText(file);
    }
  };

  const filteredTags = tags.filter(tag =>
    tag.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tag.display_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTypeBadgeColor = (type) => {
    const colors = {
      product: 'bg-blue-100 text-blue-800',
      service: 'bg-green-100 text-green-800',
      rental: 'bg-purple-100 text-purple-800',
      customer: 'bg-yellow-100 text-yellow-800',
      order: 'bg-red-100 text-red-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Tags Management</h1>
            <p className="text-gray-500 mt-1">Organize and manage tags across your marketplace</p>
          </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button variant="outline" onClick={() => setIsImportModalOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Import
          </Button>
          <Button onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Tag
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Tags</CardTitle>
              <TrendingUp className="h-4 w-4 text-gray-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_tags}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Most Used Tag</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-semibold">
                {stats.most_used_tags[0]?.name || 'N/A'}
              </div>
              {stats.most_used_tags[0] && (
                <p className="text-xs text-gray-500 mt-1">
                  {stats.most_used_tags[0].usage_count} uses
                </p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Tag Types</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{Object.keys(stats.tags_by_type).length}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search tags..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {TAG_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tags Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-8">Loading tags...</div>
          ) : filteredTags.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No tags found. Create your first tag to get started!
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tag Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Usage Count</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTags.map((tag) => (
                  <TableRow key={tag.id}>
                    <TableCell className="font-medium">
                      <Badge 
                        variant="secondary" 
                        style={{ 
                          backgroundColor: tag.color ? `${tag.color}20` : undefined,
                          borderColor: tag.color || undefined,
                          color: tag.color || undefined
                        }}
                      >
                        {tag.display_name}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={getTypeBadgeColor(tag.tag_type)}>
                        {TAG_TYPES.find(t => t.value === tag.tag_type)?.label || tag.tag_type}
                      </Badge>
                    </TableCell>
                    <TableCell>{tag.usage_count}</TableCell>
                    <TableCell>{new Date(tag.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(tag)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteTag(tag)}
                          disabled={tag.usage_count > 0}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Tag</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Tag Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter tag name (letters, numbers, hyphens only)"
                maxLength={formData.tag_type === 'order' || formData.tag_type === 'draft_order' ? 40 : 255}
              />
              <p className="text-xs text-gray-500 mt-1">
                Max {formData.tag_type === 'order' || formData.tag_type === 'draft_order' ? 40 : 255} characters
              </p>
            </div>
            <div>
              <Label htmlFor="tag_type">Tag Type</Label>
              <Select value={formData.tag_type} onValueChange={(value) => setFormData({ ...formData, tag_type: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TAG_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="color">Color (Optional)</Label>
              <div className="flex gap-2 flex-wrap mt-2">
                {TAG_COLORS.map(color => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, color: color.value })}
                    className={`w-8 h-8 rounded border-2 ${formData.color === color.value ? 'border-gray-900' : 'border-gray-300'}`}
                    style={{ backgroundColor: color.value }}
                    title={color.label}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, color: null })}
                  className={`w-8 h-8 rounded border-2 ${!formData.color ? 'border-gray-900' : 'border-gray-300'} bg-white flex items-center justify-center`}
                  title="No color"
                >
                  <span className="text-xs">✕</span>
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateTag} disabled={!formData.name.trim()}>
              Create Tag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Tag</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-name">Tag Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter tag name"
                maxLength={formData.tag_type === 'order' || formData.tag_type === 'draft_order' ? 40 : 255}
              />
            </div>
            <div>
              <Label>Tag Type</Label>
              <Input value={TAG_TYPES.find(t => t.value === formData.tag_type)?.label} disabled />
            </div>
            <div>
              <Label htmlFor="edit-color">Color (Optional)</Label>
              <div className="flex gap-2 flex-wrap mt-2">
                {TAG_COLORS.map(color => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, color: color.value })}
                    className={`w-8 h-8 rounded border-2 ${formData.color === color.value ? 'border-gray-900' : 'border-gray-300'}`}
                    style={{ backgroundColor: color.value }}
                    title={color.label}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, color: null })}
                  className={`w-8 h-8 rounded border-2 ${!formData.color ? 'border-gray-900' : 'border-gray-300'} bg-white flex items-center justify-center`}
                  title="No color"
                >
                  <span className="text-xs">✕</span>
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateTag} disabled={!formData.name.trim()}>
              Update Tag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={isImportModalOpen} onOpenChange={setIsImportModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Tags from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="csv-file">Upload CSV File</Label>
              <Input
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-2">
                CSV should have columns: name, tag_type, color (optional)
              </p>
            </div>
            {importCsvContent && (
              <div>
                <Label>Preview (first 200 characters)</Label>
                <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-auto max-h-32">
                  {importCsvContent.substring(0, 200)}...
                </pre>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsImportModalOpen(false); setImportCsvContent(''); }}>
              Cancel
            </Button>
            <Button onClick={handleImport} disabled={!importCsvContent}>
              Import Tags
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default Tags;
